import { Align, Button, Form, IFormRender, Justify, Label, Row } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import CertificationPickerFormField from 'components/domain/certifications/CertificationPickerFormField/CertificationPickerFormField';
import { t } from 'i18next';
import { isNil } from 'lodash';
import { ReactNode, useRef } from 'react';
import { ClassName } from 'types/ClassName';
import { isoTimeStampUtc } from 'utils/dateUtils';
import { v_require } from 'utils/validations';
import { useMutateCertificationEquipmentLinks } from '../useMutateCertificationEquipmentLinks';
import './EquipmentCertificationLinkForm.scss';
import { EquipmentCertificationsDetailsData } from './EquipmentCertificationLinkFormDialog';

export interface IEquipmentCertificationLinkFormProps {
  className?: ClassName;
  equipmentId: string;
  equipmentLink?: EquipmentCertificationsDetailsData | undefined;
  onDelete: () => void;
  onManage: () => void;
  onSubmit: () => void;
}

interface IEquipmentCertificationLinkFormData {
  certificationId?: string;
}

const EquipmentCertificationLinkForm = (props: IEquipmentCertificationLinkFormProps) => {
  const { className, equipmentId, equipmentLink, onDelete, onManage, onSubmit } = props;

  const formData = useRef<IEquipmentCertificationLinkFormData>({
    certificationId: equipmentLink?.certificationId,
  }).current;

  const { createCertificationEquipmentLink, updateCertificationEquipmentLink } = useMutateCertificationEquipmentLinks();

  function renderFormFields({ ...form }: IFormRender<IEquipmentCertificationLinkFormData>): ReactNode {
    return (
      <>
        <Row align={Align.START} justify={Justify.SPACE_BETWEEN}>
          <Label className="mt-3">{t('Certification')}</Label>
          <Button type="link" onClick={onManage}>
            {t('Manage')}
          </Button>
        </Row>

        <CertificationPickerFormField form={form} name="certificationId" validations={[{ validation: v_require }]} />

        <Button type="primary" onClick={form.handleSubmit} className="m-2">
          {t('Submit')}
        </Button>
        {!isNil(equipmentLink) && (
          <div className={'delete-button mx-2'} onClick={onDelete}>
            {t('Delete')}
          </div>
        )}
      </>
    );
  }

  async function onSubmitForm(data: IEquipmentCertificationLinkFormData) {
    if (!isNil(data.certificationId)) {
      if (!isNil(equipmentLink)) {
        if (data.certificationId !== equipmentLink.certificationId && equipmentId === equipmentLink.equipmentId) {
          await updateCertificationEquipmentLink({ id: equipmentLink.id, deletedOn: isoTimeStampUtc() });

          await createCertificationEquipmentLink({
            equipmentId: equipmentLink.equipmentId,
            certificationId: data.certificationId,
            createdOn: isoTimeStampUtc(),
          });
        }
      } else {
        await createCertificationEquipmentLink({
          equipmentId: equipmentId,
          certificationId: data.certificationId,
          createdOn: isoTimeStampUtc(),
        });
      }
      onSubmit();
    }
  }

  const classes = classNames('equipment-certification-link-form', className);

  return (
    <Form
      className={classes}
      allowMultipleSubmissions={true}
      data={formData}
      onSubmit={onSubmitForm}
      render={renderFormFields}
    />
  );
};

export default EquipmentCertificationLinkForm;
